import { createContext, useState, useEffect, useContext, useCallback, useMemo } from "react";
import { getLocations } from "../services/locationsApi";
import SmartLocationService from "../services/smartLocationService";
import { useAuth } from "./AuthContext";

const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
    const [locations, setLocations] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(true);
    // Estado global para la ubicación seleccionada - explícitamente inicializado como null
    const [selectedGlobalLocation, setSelectedGlobalLocation] = useState(null);
    // Flag para rastrear si la ubicación fue seleccionada por GPS
    const [isLocationFromGPS, setIsLocationFromGPS] = useState(false);
    // Flag persistente: el usuario eligió explícitamente "sin ubicación"
    const [noLocationSelected, setNoLocationSelected] = useState(false);

    // Inicializar el estado global desde localStorage al montar
    useEffect(() => {
        const storedLocationJSON = localStorage.getItem("selectedLocation");
        if (storedLocationJSON) {
            try {
                const parsedLocation = JSON.parse(storedLocationJSON);
                setSelectedGlobalLocation(parsedLocation);
            } catch (error) {
                console.error("Error parsing stored location:", error);
                localStorage.removeItem("selectedLocation");
            }
        }
        // Inicializar bandera de no selección
        const noSel = localStorage.getItem("noLocationSelected");
        if (noSel === 'true') {
            setNoLocationSelected(true);
        }
    }, []);

    // Función para actualizar la ubicación global - permite explícitamente establecer null
    const updateGlobalLocation = useCallback((location, isFromGPS = false) => {
        setSelectedGlobalLocation(location); // Puede ser null para "sin ubicación"
        setIsLocationFromGPS(isFromGPS);

        if (location) {
            setNoLocationSelected(false);
            localStorage.setItem("selectedLocation", JSON.stringify(location));
            localStorage.removeItem("noLocationSelected");
        } else {
            // Si se pasa null, eliminamos la ubicación del localStorage y marcamos no selección
            localStorage.removeItem("selectedLocation");
            setNoLocationSelected(true);
            localStorage.setItem("noLocationSelected", 'true');
        }
    }, []);

    // Función para limpiar la ubicación (establecerla explícitamente a null)
    const clearGlobalLocation = useCallback(() => {
        setSelectedGlobalLocation(null);
        setIsLocationFromGPS(false);
        setNoLocationSelected(true);
        localStorage.removeItem("selectedLocation");
        localStorage.setItem("noLocationSelected", 'true');
    }, []);

    const { user } = useAuth();

    const fetchLocations = useCallback(async () => {
        setLoadingLocations(true);
        try {
            const data = await getLocations();
            setLocations(data || []);
        } catch (error) {
            console.error("Error fetching locations:", error);
        } finally {
            setLoadingLocations(false);
        }
    }, []);

    // Fetch inicial + reintento cuando cambia la sesión (login tardío, refresh
    // de token, etc.) por si el primer intento se hizo sin auth.
    useEffect(() => {
        fetchLocations();
    }, [fetchLocations, user]);

    // Derivado: lista plana de nombres (reemplaza llamadas a listLocationNames)
    const locationNames = useMemo(
        () => locations.map((loc) => loc.name),
        [locations]
    );

    // Lookup local por nombre (reemplaza llamadas redundantes a getLocationByName)
    const getLocationByName = useCallback(
        (name) => locations.find((loc) => loc.name === name) || null,
        [locations]
    );

    // 🆕 Función centralizada que usa SmartLocationService
    const detectLocationFromGPS = useCallback(async (latitude, longitude) => {
        if (!locations || locations.length === 0) {
            return null;
        }

        return await SmartLocationService.detectLocationFromGPS(latitude, longitude, locations);
    }, [locations]);

    // 🆕 Funciones de utilidad centralizadas
    const generateSmartLocation = useCallback((detectionResult, coordinates, type = 'gps') => {
        switch (type) {
            case 'gps':
                return SmartLocationService.generateSmartLocation(detectionResult, coordinates);
            case 'manual':
                return SmartLocationService.generateManualSmartLocation(coordinates); // coordinates = selectedLocation en este caso
            case 'custom':
                return SmartLocationService.generateCustomSmartLocation(coordinates); // coordinates = customText en este caso
            default:
                return SmartLocationService.generateSmartLocation(detectionResult, coordinates);
        }
    }, []);

    const canUseForFiltering = useCallback((smartLocation) => {
        return SmartLocationService.canUseForFiltering(smartLocation);
    }, []);

    const getLocationStatusMessage = useCallback((detectionResult) => {
        return SmartLocationService.getLocationStatusMessage(detectionResult);
    }, []);

    const value = {
        locations,
        locationNames,
        loadingLocations,
        getLocationByName,
        refreshLocations: fetchLocations,
        // Estado global de ubicación y funciones relacionadas
        selectedGlobalLocation,
        isLocationFromGPS,
        noLocationSelected,
        updateGlobalLocation,
        clearGlobalLocation, // Nueva función para limpiar explícitamente la ubicación
        // Nuevas funciones centralizadas
        detectLocationFromGPS,
        generateSmartLocation,
        canUseForFiltering,
        getLocationStatusMessage,
        // Funciones directas del servicio
        SmartLocationService
    };

    return (
        <LocationContext.Provider value={value}>
            {children}
        </LocationContext.Provider>
    );
};

export const useLocations = () => {
    return useContext(LocationContext);
};
